import { PlusIcon } from "lucide-react";

import type { HistoryImportConfig } from "./historyImportConfig";
import type { GridSelectionStore } from "./grid-selection-store";

import { Button } from "@/components/ui/button";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";

export const isExactOneSelected = (selection: GridSelectionStore<string | number>): boolean =>
  selection.mode === "selected" && selection.selectedIds.length === 1;

export const assertExactOneSelection = (
  selection: GridSelectionStore<string | number>,
  entityLabel: string,
): boolean => {
  if (isExactOneSelected(selection)) {
    return true;
  }
  displayToast(`Select exactly one ${entityLabel} to import into this cell.`, ToastType.error);
  return false;
};

export const HistoryImportActionButton = <TPayload,>({
  config,
  selectedCount,
  onClick,
}: {
  config: HistoryImportConfig<TPayload>;
  selectedCount: number;
  onClick: () => void;
}) => (
  <Button
    size="sm"
    onClick={onClick}
    isLoading={config.isImporting}
    disabled={config.selectionLimit === 1 && selectedCount !== 1}
  >
    <PlusIcon className="mr-1.5 h-4 w-4" />
    {config.isImporting
      ? `Import ${Math.round(config.importProgress ?? 0)}%`
      : (config.actionLabel ?? "Add to Sheet")}
  </Button>
);

export const submitHistoryImport = async <TPayload, TId extends string | number>({
  config,
  selection,
  entityLabel,
  buildPayload,
}: {
  config: HistoryImportConfig<TPayload>;
  selection: GridSelectionStore<TId>;
  entityLabel: string;
  buildPayload: () => TPayload | null;
}): Promise<boolean> => {
  if (config.selectionLimit === 1 && !assertExactOneSelection(selection, entityLabel)) {
    return false;
  }

  const payload = buildPayload();
  if (!payload) {
    displayToast(`Could not build ${entityLabel} import payload`, ToastType.error);
    return false;
  }

  const didImport = await config.submitImport(payload);
  if (!didImport) {
    return false;
  }

  selection.clear();
  config.handleImportSuccess();
  return true;
};
